import React, { useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useDispatch, useSelector } from 'react-redux';

import { ReduxStates } from './redux/reduxTypes';
import * as CopyDataActions from './redux/actions/copyData';
import * as DataActions from './redux/actions/data';
import * as UserActions from './redux/actions/user';
import { fetchCall } from './services/api';
import { setClient } from './redux/actions/client';
import { fetchUnreadNotesCount } from './pages/Notes/helpers';

const Initialize = (): React.ReactElement | null => {
  const { authState } = useOktaAuth();
  const dispatch = useDispatch();
  const config = useSelector((state: ReduxStates) => state.config);

  const fetchCopyData = async (): Promise<any> => {
    try {
      const data = await fetchCall(`${process.env.PUBLIC_URL}/data/en.json`);
      return data;
    } catch (error: any) {
      throw new Error(error);
    }
  };

  const fetchCurrencies = async (): Promise<any> => {
    try {
      const currencies = await fetchCall(`${config.react_app_currency_api_base_url}/admin/currency/`);
      return currencies;
    } catch (error: any) {
      throw new Error(error);
    }
  };

  const fetchEnvironment = async (): Promise<any> => {
    try {
      const environment = await fetchCall(`${config.react_app_api_base_url}/environment`);
      return environment;
    } catch (error: any) {
      throw new Error(error);
    }
  };

  const fetchUserInfo = async (): Promise<any> => {
    try {
      const userInfo = await fetchCall(`${config.react_app_api_base_url}/user/info`);
      return userInfo;
    } catch (error: any) {
      throw new Error(error);
    }
  };

  const fetchMessagingInfo = async (): Promise<any> => {
    try {
      const messagingInfo = await fetchCall(`${config.react_app_api_base_url}/client/metadata/share/digitalshelf/info`);
      return messagingInfo;
    } catch (error: any) {
      throw new Error(error);
    }
  };

  const fetchClientInfo = async (): Promise<any> => {
    try {
      const metadata = await fetchCall(`${config.react_app_api_base_url}/client/metadata/`);
      return metadata;
    } catch (error: any) {
      throw new Error(error);
    }
  };

  const fetchAvailableRanges = async (): Promise<any> => {
    try {
      const ranges = await fetchCall(`${config.react_app_api_base_url}/client/metadata/available`);
      return ranges;
    } catch (error: any) {
      throw new Error(error);
    }
  };

  useEffect(() => {
    if (authState?.isAuthenticated) {
      fetchCopyData()
        .then((data) => {
          dispatch(CopyDataActions.setCopyData(data));
        })
        .catch((error) => {
          console.log('error fetching copy data:', error);
        });
      fetchCurrencies()
        .then((data) => {
          dispatch(DataActions.setCurrencies(data));
        })
        .catch((error) => {
          console.log('error fetching currencies:', error);
        });
      fetchEnvironment()
        .then((data) => {
          dispatch(DataActions.setEnvironment(data));
        })
        .catch((error) => {
          console.log('error fetching environment:', error);
        });
      fetchUserInfo()
        .then((data) => {
          dispatch(UserActions.setUserModules(data.userModules));
          dispatch(UserActions.setUserID(data.userEmail));
          dispatch(UserActions.setUserLogin(true));
        })
        .catch((error) => {
          console.log('error fetching user info:', error);
        });
      fetchMessagingInfo()
        .then((data) => {
          dispatch(DataActions.setMessagingInfo(data));
        })
        .catch((error) => {
          console.log('error fetching messaging info:', error);
        });
      fetchClientInfo()
        .then((data) => {
          dispatch(DataActions.setMetaData(data));
          dispatch(setClient(data.shortName));
          dispatch(DataActions.setDefaultCurrency(data.defaultCurrency));
        })
        .catch((error) => {
          console.log('error fetching client info:', error);
        });
      fetchUnreadNotesCount()
        .then((data) => {
          dispatch(DataActions.setUnreadNotes(data.newMsgsCount));
        })
        .catch((error) => {
          console.log('error fetching unread notes count:', error);
        });
      fetchAvailableRanges()
        .then((data) => {
          dispatch(DataActions.setDateRangeLimit(data));
        })
        .catch((error) => {
          console.log('error fetching date range limits:', error);
        });
    }
  }, [authState]);

  return <></>;
};

export default Initialize;
